import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Camera,
  CheckCircle2,
  MapPin,
  Calendar,
  Building2,
  AlertCircle,
  RefreshCw,
  ShieldCheck,
  Stethoscope,
  Phone,
  ArrowRight,
  ArrowLeft,
  Clock,
} from 'lucide-react';
import { presencaService } from '../services/presenca.service';
import { getCustomerApiBaseURL } from '../utils/apiConfig';
import { formatCPF, formatPhone } from '../utils/formatters';
import type {
  DadosPresencaAgendamentoContract,
  ColaboradorPortalPerfilContract,
} from '@sstexames/shared-types';

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

export default function ConfirmarPresenca() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isEmbedded = useMemo(() => {
    const ref = searchParams.get('ref');
    return ref === 'embeded' || ref === 'embedded';
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dados, setDados] = useState<DadosPresencaAgendamentoContract | null>(null);

  // Navegação em 4 Etapas (Wizard Mobile/Desktop)
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);

  // Escolha de presença/horário
  const [tipoPresenca, setTipoPresenca] = useState<'ja_chegou' | 'horario_previsto' | null>(null);
  const [horarioEscolhido, setHorarioEscolhido] = useState<string>('');
  const [erroValidacaoHorario, setErroValidacaoHorario] = useState<string | null>(null);
  const [showRecusaModal, setShowRecusaModal] = useState(false);
  const [motivoRecusa, setMotivoRecusa] = useState('');
  const [submittingRecusa, setSubmittingRecusa] = useState(false);

  // Ações e submissões
  const [submittingIntencao, setSubmittingIntencao] = useState(false);
  const [submittingPresenca, setSubmittingPresenca] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Horário atual formatado (HH:mm)
  const currentTimeStr = new Date().toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  // Detecção de mobile para auditoria no backend
  const isMobileDevice =
    typeof navigator !== 'undefined' &&
    /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      navigator.userAgent,
    );

  // Estados da Selfie
  const [selfieBlob, setSelfieBlob] = useState<Blob | null>(null);
  const [selfiePreviewUrl, setSelfiePreviewUrl] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Parada de faixas do MediaStream
  const stopCameraStreamOnly = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          // ignora erro ao parar faixa
        }
      });
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Encerramento completo da câmera
  const stopCamera = useCallback(() => {
    stopCameraStreamOnly();
    setIsCameraActive(false);
    setCameraStarting(false);
  }, [stopCameraStreamOnly]);

  const carregarDados = useCallback(async () => {
    if (!token) {
      setError('Token de presença não informado');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await presencaService.getDados(token);
      setDados(data);

      if (data.horario) {
        setHorarioEscolhido(data.horario);
      }
      if (data.tipo_confirmacao === 'ja_chegou' || data.tipo_confirmacao === 'horario_previsto') {
        setTipoPresenca(data.tipo_confirmacao);
      }

      // Direcionamento inteligente da etapa inicial baseado no estado atual
      if (data.confirmou_presenca) {
        setCurrentStep(4);
      } else {
        setCurrentStep(1);
      }
    } catch (err: unknown) {
      const errorObj = err as ApiError;
      setError(
        errorObj?.response?.data?.message ||
          'Não foi possível carregar os dados do agendamento. Link inválido ou expirado.',
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    document.title = 'Portal do Colaborador - SST Exames';
    carregarDados();
    return () => {
      stopCamera();
    };
  }, [carregarDados, stopCamera]);

  // Callback ref para anexar o stream ao elemento de vídeo no exato momento da montagem no DOM
  const videoCallbackRef = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    if (node && mediaStreamRef.current) {
      if (node.srcObject !== mediaStreamRef.current) {
        node.srcObject = mediaStreamRef.current;
      }
      node.onloadedmetadata = () => {
        node.play().catch((err) => {
          console.warn('Erro ao reproduzir stream de vídeo na inicialização:', err);
        });
      };
      node.play().catch(() => {});
    }
  }, []);

  // Abertura de câmera com stream com fallback resiliente e garantia de anexo
  const startCamera = async () => {
    setCameraError(null);
    setCameraStarting(true);
    setIsCameraActive(true);
    stopCameraStreamOnly();

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Navegador não possui suporte à API de câmera');
      }

      let stream: MediaStream;
      try {
        // Tenta primeiro com câmera frontal e resolução ideal
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
      } catch (constraintErr) {
        console.warn('Tentativa com facingMode falhou, tentando modo genérico:', constraintErr);
        // Fallback genérico para webcams sem suporte a constraints avançadas
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
      }

      mediaStreamRef.current = stream;

      // Se o elemento de vídeo já estiver montado no DOM, conecta imediatamente
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch((e) => console.warn('play error:', e));
        };
        videoRef.current.play().catch(() => {});
      }
    } catch (err: unknown) {
      console.error('Erro ao acessar a câmera:', err);
      stopCamera();
      const domErr = err as { name?: string };
      const isDenied =
        domErr?.name === 'NotAllowedError' || domErr?.name === 'PermissionDeniedError';
      setCameraError(
        isDenied
          ? 'Permissão de câmera negada. Permita o acesso à câmera nas configurações do navegador ou selecione uma foto salva.'
          : 'Não foi possível inicializar a câmera do dispositivo. Você pode selecionar uma foto usando o botão abaixo.',
      );
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } finally {
      setCameraStarting(false);
    }
  };

  // Observa mudanças em isCameraActive para garantir reprodução do vídeo
  useEffect(() => {
    if (isCameraActive && videoRef.current && mediaStreamRef.current) {
      const video = videoRef.current;
      if (video.srcObject !== mediaStreamRef.current) {
        video.srcObject = mediaStreamRef.current;
      }
      video.onloadedmetadata = () => {
        video.play().catch((err) => {
          console.warn('Erro ao reproduzir stream:', err);
        });
      };
      video.play().catch(() => {});
    }
  }, [isCameraActive]);

  const capturePhotoFromStream = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;

    if (width === 0 || height === 0) {
      setCameraError('Aguarde a câmera carregar a imagem antes de tirar a foto.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Espelha horizontalmente se a câmera for frontal para ficar idêntica à prévia
    ctx.translate(width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          if (selfiePreviewUrl) {
            URL.revokeObjectURL(selfiePreviewUrl);
          }
          setSelfieBlob(blob);
          setSelfiePreviewUrl(URL.createObjectURL(blob));
          stopCamera();
        }
      },
      'image/jpeg',
      0.88,
    );
  };

  // Upload por arquivo/galeria ou câmera nativa
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (selfiePreviewUrl) {
        URL.revokeObjectURL(selfiePreviewUrl);
      }
      setSelfieBlob(file);
      setSelfiePreviewUrl(URL.createObjectURL(file));
      stopCamera();
    }
  };

  // Verificações de data do agendamento (horário de São Paulo)
  const hojeSPStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  const dataAgendamentoYMD = dados?.data_agendamento ? dados.data_agendamento.split('T')[0] : '';
  const isAgendamentoHoje = Boolean(dataAgendamentoYMD && dataAgendamentoYMD === hojeSPStr);
  const isAgendamentoFuturo = Boolean(dataAgendamentoYMD && dataAgendamentoYMD > hojeSPStr);
  const isAgendamentoPassado = Boolean(dataAgendamentoYMD && dataAgendamentoYMD < hojeSPStr);

  const horarioInicioClinica = dados?.clinica?.horario_inicio || '08:00';
  const horarioFimClinica = dados?.clinica?.horario_fim || '17:00';
  const intervaloIniClinica = dados?.clinica?.intervalo_ini || null;
  const intervaloFimClinica = dados?.clinica?.intervalo_fim || null;

  // Sugestões de horários baseadas no funcionamento da clínica
  // Se for hoje, filtra estritamente horários futuros
  const getHorariosSugeridos = useCallback(() => {
    const [startH, startM] = horarioInicioClinica.split(':').map(Number);
    const [endH, endM] = horarioFimClinica.split(':').map(Number);

    const startMinutes = (startH || 8) * 60 + (startM || 0);
    const endMinutes = (endH || 17) * 60 + (endM || 0);

    const currentNowMinutes = isAgendamentoHoje
      ? (() => {
          const nowStr = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
          const [nowH, nowM] = nowStr.split(':').map(Number);
          return (nowH || 0) * 60 + (nowM || 0);
        })()
      : -1;

    const slots: string[] = [];
    for (let m = startMinutes; m <= endMinutes; m += 30) {
      if (intervaloIniClinica && intervaloFimClinica) {
        const [iIniH, iIniM] = intervaloIniClinica.split(':').map(Number);
        const [iFimH, iFimM] = intervaloFimClinica.split(':').map(Number);
        const iIniMinutes = (iIniH || 0) * 60 + (iIniM || 0);
        const iFimMinutes = (iFimH || 0) * 60 + (iFimM || 0);
        if (m >= iIniMinutes && m < iFimMinutes) {
          continue;
        }
      }

      if (isAgendamentoHoje && m <= currentNowMinutes) {
        continue;
      }

      const hh = String(Math.floor(m / 60)).padStart(2, '0');
      const mm = String(m % 60).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
    }

    return slots;
  }, [
    horarioInicioClinica,
    horarioFimClinica,
    intervaloIniClinica,
    intervaloFimClinica,
    isAgendamentoHoje,
  ]);

  const horariosSugeridos = getHorariosSugeridos();

  // Validação dinâmica do horário
  const validarHorario = useCallback(
    (horario: string): string | null => {
      if (!horario) return 'Informe o horário previsto.';
      if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(horario)) {
        return 'Formato de horário inválido.';
      }

      const nowStr = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      if (isAgendamentoHoje && horario <= nowStr) {
        return `O horário (${horario}) não pode ser no passado. Por favor, escolha um horário futuro ou selecione "Já estou aqui".`;
      }

      if (horario < horarioInicioClinica) {
        return `A clínica só inicia o atendimento às ${horarioInicioClinica}.`;
      }

      if (horario > horarioFimClinica) {
        return `A clínica encerra o atendimento às ${horarioFimClinica}.`;
      }

      if (intervaloIniClinica && intervaloFimClinica) {
        if (horario >= intervaloIniClinica && horario < intervaloFimClinica) {
          return `A clínica está em intervalo das ${intervaloIniClinica} às ${intervaloFimClinica}.`;
        }
      }

      return null;
    },
    [
      isAgendamentoHoje,
      horarioInicioClinica,
      horarioFimClinica,
      intervaloIniClinica,
      intervaloFimClinica,
    ],
  );

  const handleHorarioChange = (h: string) => {
    setHorarioEscolhido(h);
    const erro = validarHorario(h);
    setErroValidacaoHorario(erro);
  };

  // Fluxo 1: "Já estou aqui" -> define horário atual e vai DIRETO para a etapa 4 (selfie)
  const handleSelecionarJaEstouAqui = () => {
    const nowTime = new Date().toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
    setTipoPresenca('ja_chegou');
    setHorarioEscolhido(nowTime);
    setErroValidacaoHorario(null);
    setCurrentStep(4);
    setTimeout(() => {
      startCamera();
    }, 150);
  };

  // Fluxo 2: "Pretendo ir em um horário específico" -> vai para a Etapa 3 (painel de horários)
  const handleSelecionarPretendoIrHorario = () => {
    setTipoPresenca('horario_previsto');
    setErroValidacaoHorario(null);
    if (!horarioEscolhido && horariosSugeridos.length > 0) {
      setHorarioEscolhido(horariosSugeridos[0]);
    }
    setCurrentStep(3);
  };

  // Confirmação do horário na Etapa 3 -> NÃO exige selfie! Confirma intenção e salva horário previsto (status 79 Confirmado)
  const handleConfirmarHorarioPrevisto = async () => {
    const erro = validarHorario(horarioEscolhido);
    if (erro) {
      setErroValidacaoHorario(erro);
      return;
    }
    if (!token) return;

    try {
      setSubmittingIntencao(true);
      setError(null);
      const res = await presencaService.confirmarIntencao(token, horarioEscolhido);
      setTipoPresenca('horario_previsto');
      setSuccessMessage(
        `Previsão registrada com sucesso para às ${horarioEscolhido}! Ao chegar na clínica parceira, acerte sua chegada definitiva.`,
      );

      if (dados) {
        setDados({
          ...dados,
          confirmou_intencao: true,
          horario: horarioEscolhido,
          tipo_confirmacao: 'horario_previsto',
          status: {
            id: res.status_id,
            valor: 'confirmado',
            descricao: res.status_descricao,
          },
        });
      }
    } catch (err: unknown) {
      const errorObj = err as ApiError;
      setError(
        errorObj?.response?.data?.message ||
          'Erro ao registrar previsão de horário de comparecimento.',
      );
    } finally {
      setSubmittingIntencao(false);
    }
  };

  // Envio final da selfie na Etapa 3/4
  const handleComprovarPresenca = async () => {
    if (!token || !selfieBlob) {
      setError('Por favor, tire ou selecione sua selfie antes de confirmar.');
      return;
    }

    if (isAgendamentoPassado) {
      setError('A data agendada para este exame já passou. Não é mais possível realizar a confirmação de presença.');
      return;
    }

    try {
      setSubmittingPresenca(true);
      setError(null);
      setSuccessMessage(null);

      const res = await presencaService.comprovarPresenca(token, {
        file: selfieBlob,
        isMobile: isMobileDevice,
        horario: horarioEscolhido || currentTimeStr,
        tipoConfirmacao: 'ja_chegou',
      });

      const finalSelfieUrl = res.selfie_url || selfiePreviewUrl;
      stopCamera();
      setSelfieBlob(null);
      setSuccessMessage(res.message);

      if (dados) {
        setDados({
          ...dados,
          confirmou_presenca: true,
          confirmou_intencao: true,
          presenca_em: new Date().toISOString(),
          selfie_path: finalSelfieUrl,
          horario: res.horario || horarioEscolhido,
          tipo_confirmacao: res.tipo_confirmacao || tipoPresenca,
          status: {
            id: res.status_id,
            valor: 'presente',
            descricao: res.status_descricao,
          },
        });
        setCurrentStep(4);
      }
    } catch (err: unknown) {
      const errorObj = err as ApiError;
      setError(
        errorObj?.response?.data?.message ||
          'Erro ao registrar presença com a foto.',
      );
    } finally {
      setSubmittingPresenca(false);
    }
  };

  // Redireciona o colaborador de volta para a listagem oficial de seus exames
  const handleVoltarParaListagem = () => {
    if (isEmbedded) return;
    if (dados?.colaborador) {
      let perfilParaSalvar: ColaboradorPortalPerfilContract;
      const existingSaved = localStorage.getItem('portal_colaborador_perfil');
      if (existingSaved) {
        try {
          const parsed = JSON.parse(existingSaved);
          perfilParaSalvar = {
            ...parsed,
            id: dados.colaborador.id,
            nome: dados.colaborador.nome,
            cpf: dados.colaborador.cpf,
          };
        } catch {
          perfilParaSalvar = {
            id: dados.colaborador.id,
            nome: dados.colaborador.nome,
            cpf: dados.colaborador.cpf,
            empresa: {
              id: dados.empresa.id,
              razao_social: dados.empresa.razao_social || dados.empresa.nome_fantasia || '',
              nome_fantasia: dados.empresa.nome_fantasia,
              cnpj: '',
            },
            is_candidato: false,
          };
        }
      } else {
        perfilParaSalvar = {
          id: dados.colaborador.id,
          nome: dados.colaborador.nome,
          cpf: dados.colaborador.cpf,
          empresa: {
            id: dados.empresa.id,
            razao_social: dados.empresa.razao_social || dados.empresa.nome_fantasia || '',
            nome_fantasia: dados.empresa.nome_fantasia,
            cnpj: '',
          },
          is_candidato: false,
        };
      }
      localStorage.setItem('portal_colaborador_perfil', JSON.stringify(perfilParaSalvar));
    }
    navigate('/portal-colaborador');
  };

  // Registro de ausência informada
  const handleRecusarPresenca = async () => {
    if (!token) return;
    try {
      setSubmittingRecusa(true);
      setError(null);
      await presencaService.recusarPresenca(token, motivoRecusa);
      setShowRecusaModal(false);
      setSuccessMessage('Sua ausência foi comunicada ao RH com sucesso.');

      if (dados) {
        setDados({
          ...dados,
          confirmou_intencao: false,
          tipo_confirmacao: 'nao_vai',
          status: {
            id: 21,
            valor: 'faltou',
            descricao: 'Não Compareceu',
          },
        });
      }
    } catch (err: unknown) {
      const errorObj = err as ApiError;
      setError(
        errorObj?.response?.data?.message ||
          'Erro ao registrar que não comparecerá ao exame.',
      );
    } finally {
      setSubmittingRecusa(false);
    }
  };

  const formatDateBR = (dateStr: string) => {
    if (!dateStr) return 'Hoje';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <img src="/sst_logo.svg" alt="SST Exames" className="h-9 w-auto object-contain mb-1" />
          <div className="flex items-center gap-2 text-emerald-600">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <p className="text-sm font-medium text-gray-700">
              Carregando dados do agendamento...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !dados) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-6 shadow-sm border border-gray-200 text-center">
          <img src="/sst_logo.svg" alt="SST Exames" className="h-8 w-auto object-contain mx-auto mb-4" />
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-900 mb-2">Acesso Indisponível</h2>
          <p className="text-sm text-gray-600 mb-6">{error}</p>
          <button
            onClick={carregarDados}
            className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold shadow-sm transition"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const { colaborador, empresa, clinica, exames, status, confirmou_presenca } = dados!;

  const getStatusBadge = () => {
    switch (status.valor) {
      case 'presente':
        return 'bg-emerald-600 text-white font-bold shadow-xs';
      case 'confirmado':
        return 'bg-emerald-50 text-emerald-800 border border-emerald-300 font-semibold';
      case 'realizado':
        return 'bg-purple-50 text-purple-700 border border-purple-200 font-semibold';
      case 'faltou':
        return 'bg-rose-50 text-rose-700 border border-rose-200 font-semibold';
      case 'cancelado':
        return 'bg-gray-100 text-gray-600 border border-gray-200 font-semibold';
      default:
        return 'bg-blue-50 text-blue-700 border border-blue-200 font-semibold';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col text-gray-800">
      {/* Barra de Topo Padrão SST Exames (Idêntica ao Portal do Colaborador) */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-xs">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/sst_logo.svg" alt="SST Exames" className="h-8 w-auto object-contain" />
            <div className="h-5 w-px bg-gray-200 hidden sm:block" />
            <span className="text-xs sm:text-sm font-semibold text-gray-700">
              Portal do Colaborador
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-emerald-700 font-semibold bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200 flex items-center gap-1.5 shadow-2xs">
              <ShieldCheck className="w-4 h-4 text-emerald-600" /> Acesso Seguro
            </span>
          </div>
        </div>
      </header>

      {/* Conteúdo Principal em 3 Etapas */}
      <main className="flex-1 max-w-2xl w-full mx-auto p-4 sm:p-6 space-y-4">
        {/* Mensagens de Feedback */}
        {error && (
          <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl flex items-start gap-3 text-rose-800 text-sm shadow-xs">
            <AlertCircle className="w-5 h-5 flex-shrink-0 text-rose-500 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-start gap-3 text-emerald-800 text-sm shadow-xs">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-600 mt-0.5" />
            <span className="font-medium">{successMessage}</span>
          </div>
        )}

        {/* Stepper / Indicador de Etapas */}
        <div className="bg-white rounded-2xl border border-gray-200 p-3.5 shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-800">
              {confirmou_presenca ? (
                <>
                  Status:{' '}
                  <span className="text-emerald-700 font-extrabold">
                    Confirmação Definitiva Concluída
                  </span>
                </>
              ) : (
                <>
                  Etapa {currentStep} de 4:{' '}
                  <span className="text-emerald-700">
                    {currentStep === 1 && 'Dados Gerais'}
                    {currentStep === 2 && 'Comparecimento'}
                    {currentStep === 3 && 'Horário Previsto'}
                    {currentStep === 4 && 'Selfie de Confirmação'}
                  </span>
                </>
              )}
            </span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              {confirmou_presenca ? 'Concluído' : `Passo ${currentStep} de 4`}
            </span>
          </div>

          {/* Barra de Progresso */}
          <div className="grid grid-cols-4 gap-1.5 pt-0.5">
            <button
              type="button"
              disabled={confirmou_presenca}
              onClick={() => {
                if (!confirmou_presenca) setCurrentStep(1);
              }}
              className={`h-2 rounded-full transition-all ${
                confirmou_presenca
                  ? 'bg-emerald-600 cursor-default'
                  : currentStep >= 1
                  ? 'bg-emerald-600 cursor-pointer'
                  : 'bg-gray-200 cursor-pointer'
              }`}
              title="Etapa 1: Dados Gerais"
            />
            <button
              type="button"
              disabled={confirmou_presenca || !isAgendamentoHoje}
              onClick={() => {
                if (!confirmou_presenca && isAgendamentoHoje) setCurrentStep(2);
              }}
              className={`h-2 rounded-full transition-all ${
                confirmou_presenca
                  ? 'bg-emerald-600 cursor-default'
                  : currentStep >= 2
                  ? 'bg-emerald-600 cursor-pointer'
                  : !isAgendamentoHoje
                  ? 'bg-gray-200 cursor-not-allowed'
                  : 'bg-gray-200 cursor-pointer'
              }`}
              title="Etapa 2: Comparecimento"
            />
            <button
              type="button"
              disabled={confirmou_presenca || isAgendamentoPassado}
              onClick={() => {
                if (!confirmou_presenca && !isAgendamentoPassado && (tipoPresenca === 'horario_previsto' || currentStep >= 3)) {
                  setCurrentStep(3);
                }
              }}
              className={`h-2 rounded-full transition-all ${
                confirmou_presenca
                  ? 'bg-emerald-600 cursor-default'
                  : currentStep >= 3
                  ? 'bg-emerald-600 cursor-pointer'
                  : 'bg-gray-200'
              } ${!confirmou_presenca && !isAgendamentoPassado && (tipoPresenca === 'horario_previsto' || currentStep >= 3) ? 'cursor-pointer' : 'cursor-default'}`}
              title="Etapa 3: Horário Previsto"
            />
            <button
              type="button"
              disabled={confirmou_presenca || isAgendamentoPassado}
              onClick={() => {
                if (!confirmou_presenca && !isAgendamentoPassado && currentStep >= 4) setCurrentStep(4);
              }}
              className={`h-2 rounded-full transition-all ${
                confirmou_presenca
                  ? 'bg-emerald-600 cursor-default'
                  : currentStep >= 4
                  ? 'bg-emerald-600 cursor-pointer'
                  : 'bg-gray-200 cursor-default'
              } ${!confirmou_presenca && !isAgendamentoPassado && currentStep >= 4 ? 'cursor-pointer' : 'cursor-default'}`}
              title="Etapa 4: Confirmação"
            />
          </div>

          <div className="grid grid-cols-4 text-center text-[10px] font-semibold text-gray-500 pt-0.5">
            <button
              type="button"
              disabled={confirmou_presenca}
              onClick={() => {
                if (!confirmou_presenca) setCurrentStep(1);
              }}
              className={`${
                confirmou_presenca
                  ? 'cursor-default text-gray-400'
                  : 'hover:text-emerald-700 cursor-pointer'
              } ${currentStep === 1 && !confirmou_presenca ? 'text-emerald-700 font-bold' : ''}`}
            >
              1. Dados
            </button>
            <button
              type="button"
              disabled={confirmou_presenca || isAgendamentoPassado}
              onClick={() => {
                if (!confirmou_presenca && !isAgendamentoPassado) setCurrentStep(2);
              }}
              className={`${
                confirmou_presenca || isAgendamentoPassado
                  ? 'cursor-default text-gray-400'
                  : 'hover:text-emerald-700 cursor-pointer'
              } ${currentStep === 2 && !confirmou_presenca ? 'text-emerald-700 font-bold' : ''}`}
            >
              2. Opção
            </button>
            <button
              type="button"
              disabled={confirmou_presenca || isAgendamentoPassado}
              onClick={() => {
                if (!confirmou_presenca && !isAgendamentoPassado && (tipoPresenca === 'horario_previsto' || currentStep >= 3)) {
                  setCurrentStep(3);
                }
              }}
              className={`${
                confirmou_presenca || isAgendamentoPassado
                  ? 'cursor-default text-gray-400'
                  : !confirmou_presenca && (tipoPresenca === 'horario_previsto' || currentStep >= 3)
                  ? 'hover:text-emerald-700 cursor-pointer'
                  : 'cursor-default text-gray-400'
              } ${currentStep === 3 && !confirmou_presenca ? 'text-emerald-700 font-bold' : ''}`}
            >
              3. Horário
            </button>
            <button
              type="button"
              disabled={confirmou_presenca || isAgendamentoPassado}
              onClick={() => {
                if (!confirmou_presenca && !isAgendamentoPassado && currentStep >= 4) setCurrentStep(4);
              }}
              className={`${
                confirmou_presenca
                  ? 'text-emerald-700 font-bold cursor-default'
                  : currentStep >= 4
                  ? 'hover:text-emerald-700 cursor-pointer text-emerald-700 font-bold'
                  : 'cursor-default text-gray-400'
              }`}
            >
              4. {confirmou_presenca ? 'Concluído' : 'Foto'}
            </button>
          </div>
        </div>

        {/* ETAPA 1: CARD DOS DADOS GERAIS */}
        {!confirmou_presenca && currentStep === 1 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 shadow-sm space-y-4 animate-in fade-in duration-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-gray-100 gap-2">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-100">
                  SST Exames • Portal do Colaborador
                </span>
                <h1 className="text-xl sm:text-2xl font-black text-gray-900 mt-2">
                  Olá, {colaborador.nome}!
                </h1>
                <p className="text-xs text-gray-600 mt-0.5">
                  Empresa: <strong className="text-gray-800">{empresa.nome_fantasia || empresa.razao_social || 'Empresa Empregadora'}</strong>
                  {colaborador.cpf && (
                    <> • CPF: <strong className="text-gray-800">{formatCPF(colaborador.cpf)}</strong></>
                  )}
                </p>
              </div>

              <div className="self-start sm:self-center">
                <span className={`text-xs px-3 py-1 rounded-full inline-flex items-center ${getStatusBadge()}`}>
                  {status.descricao}
                </span>
              </div>
            </div>

            {/* Aviso de Confirmação Bloqueada para Agendamentos Futuros */}
            {isAgendamentoFuturo && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3 text-amber-900 text-xs shadow-xs">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-bold text-sm text-amber-950">
                    Confirmação liberada estritamente no dia do exame
                  </p>
                  <p className="text-amber-800">
                    Seu atendimento está agendado para <strong>{formatDateBR(dados.data_agendamento)}</strong>. A confirmação de presença com selfie deve ser realizada exclusivamente no dia agendado. Por favor, retorne nesta página na data indicada para registrar sua presença.
                  </p>
                </div>
              </div>
            )}

            {/* Aviso se a data do agendamento já expirou */}
            {isAgendamentoPassado && (
              <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl flex items-start gap-3 text-rose-900 text-xs shadow-xs">
                <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-bold text-sm text-rose-950">
                    Agendamento Expirado
                  </p>
                  <p className="text-rose-800">
                    A data agendada para este exame (<strong>{formatDateBR(dados.data_agendamento)}</strong>) já passou. Não é mais possível realizar a confirmação de presença.
                  </p>
                </div>
              </div>
            )}

            {/* BOTÃO DE DESTAQUE PRINCIPAL: ACIMA DAS INFORMAÇÕES */}
            <div className="pt-1">
              {!isAgendamentoPassado ? (
                <button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="w-full py-4 px-5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-black text-base rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2.5 cursor-pointer group"
                >
                  <span>Avançar para Confirmar Presença</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : !isEmbedded ? (
                <button
                  type="button"
                  onClick={handleVoltarParaListagem}
                  className="w-full py-3.5 px-4 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 font-bold text-sm rounded-2xl border border-slate-300 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-2xs"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Voltar para o Portal do Colaborador</span>
                </button>
              ) : null}
            </div>

            {/* Informações de Atendimento */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs">
              <div className="flex items-start gap-2.5 text-gray-700">
                <Calendar className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-gray-500 font-semibold block uppercase text-[10px]">Data do Atendimento</span>
                  <span className="font-bold text-gray-900 text-sm">{formatDateBR(dados.data_agendamento)}</span>
                </div>
              </div>

              <div className="flex items-start gap-2.5 text-gray-700">
                <Building2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-gray-500 font-semibold block uppercase text-[10px]">Empresa</span>
                  <span className="font-bold text-gray-900">{empresa.nome_fantasia || empresa.razao_social}</span>
                </div>
              </div>

              {clinica && (
                <div className="sm:col-span-2 flex items-start gap-2.5 text-gray-700 pt-2 border-t border-slate-200/60">
                  <MapPin className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-gray-500 font-semibold block uppercase text-[10px]">Clínica Credenciada</span>
                    <span className="font-bold text-gray-900 block text-sm">{clinica.nome}</span>
                    <span className="text-gray-600 block mt-0.5">{clinica.endereco || 'Endereço a confirmar'}</span>
                    {clinica.telefone && (
                      <a
                        href={`tel:${clinica.telefone.replace(/\D/g, '')}`}
                        className="text-emerald-700 hover:underline font-medium inline-flex items-center gap-1 mt-1 text-xs"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        {formatPhone(clinica.telefone)}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Lista de Exames */}
            {exames && exames.length > 0 && (
              <div className="pt-2">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-2 flex items-center gap-1.5">
                  <Stethoscope className="w-4 h-4 text-emerald-600" />
                  Exames previstos:
                </span>
                <div className="flex flex-wrap gap-2">
                  {exames.map((ex) => (
                    <span
                      key={ex.id}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50/70 border border-emerald-200 text-emerald-900 shadow-2xs"
                    >
                      ✓ {ex.nome}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ETAPA 2: ESCOLHER ENTRE "JÁ ESTOU AQUI" OU "INFORMAR HORÁRIO" */}
        {!confirmou_presenca && currentStep === 2 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 shadow-sm space-y-5 animate-in fade-in duration-200">
            <div className="flex items-start gap-3.5 pb-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                2
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-100">
                  Etapa 2 • Comparecimento
                </span>
                <h2 className="text-lg font-bold text-gray-900 mt-2">
                  Você vai comparecer ao exame hoje?
                </h2>
                <p className="text-xs text-gray-600 mt-1">
                  Escolha uma das opções abaixo para prosseguir:
                </p>
              </div>
            </div>

            {/* Opções Principais */}
            <div className="space-y-3.5">
              {/* Opção 1: Já estou na clínica -> Vai DIRETO para a foto (Etapa 4) */}
              <button
                type="button"
                onClick={handleSelecionarJaEstouAqui}
                className="w-full text-left p-4 rounded-2xl border-2 border-emerald-500/80 bg-emerald-50/40 hover:bg-emerald-50 hover:border-emerald-600 transition-all shadow-xs group cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3.5">
                    <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-xs shrink-0 group-hover:scale-105 transition-transform">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-sm font-bold text-gray-900 block group-hover:text-emerald-950">
                        Já estou aqui na clínica
                      </span>
                      <span className="text-xs text-emerald-700 block mt-0.5">
                        Registrar chegada agora ({currentTimeStr}) e ir direto para a foto
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-emerald-600 group-hover:translate-x-1 transition-transform shrink-0" />
                </div>
              </button>

              {/* Opção 2: Pretendo ir em um horário específico -> Vai para a Etapa 3 (Escolher horário) */}
              <button
                type="button"
                onClick={handleSelecionarPretendoIrHorario}
                className="w-full text-left p-4 rounded-2xl border border-gray-200 bg-white hover:border-emerald-500 hover:bg-emerald-50/20 transition-all shadow-2xs group cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3.5">
                    <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0 group-hover:bg-emerald-100 group-hover:text-emerald-800 transition-colors">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-sm font-bold text-gray-900 block group-hover:text-emerald-950">
                        Pretendo ir em um horário específico
                      </span>
                      <span className="text-xs text-gray-500 block mt-0.5">
                        Escolher o horário previsto de comparecimento à clínica
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-emerald-600 group-hover:translate-x-1 transition-transform shrink-0" />
                </div>
              </button>
            </div>

            {/* Navegação Inferior e Recusa */}
            <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="py-2 px-3 text-gray-600 hover:text-gray-900 font-semibold flex items-center gap-1 cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Voltar aos Dados</span>
              </button>

              <button
                type="button"
                onClick={() => setShowRecusaModal(true)}
                className="py-2 px-3 text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-lg font-semibold transition cursor-pointer"
              >
                Não poderei comparecer
              </button>
            </div>
          </div>
        )}

        {/* ETAPA 3: ESCOLHER HORÁRIO PREVISTO (ETAPA DEDICADA) */}
        {!confirmou_presenca && currentStep === 3 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 shadow-sm space-y-5 animate-in fade-in duration-200">
            <div className="flex items-start gap-3.5 pb-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                3
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-100">
                  Etapa 3 • Horário Previsto
                </span>
                <h2 className="text-lg font-bold text-gray-900 mt-2">
                  Que horas pretende comparecer?
                </h2>
                <p className="text-xs text-gray-600 mt-1">
                  Selecione o horário previsto de chegada à clínica para atendimento.
                </p>
              </div>
            </div>

            {/* Banner de Funcionamento da Clínica */}
            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 text-xs text-gray-700 flex items-start gap-2.5">
              <Building2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <span className="font-bold text-gray-900 block">
                  Atendimento da Clínica: {horarioInicioClinica} às {horarioFimClinica}
                  {intervaloIniClinica && intervaloFimClinica && (
                    <span className="font-normal text-gray-500">
                      {' '}(Intervalo: {intervaloIniClinica} às {intervaloFimClinica})
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-gray-500 block">
                  {isAgendamentoHoje
                    ? `Selecione um horário futuro a partir de agora (${currentTimeStr}) até às ${horarioFimClinica}.`
                    : `Selecione um horário entre ${horarioInicioClinica} e ${horarioFimClinica}.`}
                </span>
              </div>
            </div>

            {/* Chips de Horários Sugeridos */}
            {horariosSugeridos.length > 0 ? (
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-gray-700">
                  Horários disponíveis sugeridos:
                </label>
                <div className="flex flex-wrap gap-2">
                  {horariosSugeridos.map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => handleHorarioChange(h)}
                      className={`px-3 py-2 text-xs rounded-xl font-bold transition cursor-pointer ${
                        horarioEscolhido === h
                          ? 'bg-emerald-600 text-white shadow-xs scale-105'
                          : 'bg-white hover:bg-emerald-50 text-gray-800 border border-gray-200 hover:border-emerald-300'
                      }`}
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                Não há horários futuros disponíveis hoje na grade da clínica. Se você já estiver na clínica, volte e utilize a opção <strong>"Já estou aqui na clínica"</strong>.
              </div>
            )}

            {/* Campo manual de digitação de horário */}
            <div className="space-y-1">
              <label className="block text-xs font-semibold text-gray-700">
                Ou digite outro horário:
              </label>
              <input
                type="time"
                value={horarioEscolhido}
                min={isAgendamentoHoje && currentTimeStr > horarioInicioClinica ? currentTimeStr : horarioInicioClinica}
                max={horarioFimClinica}
                onChange={(e) => handleHorarioChange(e.target.value)}
                className="w-full p-2.5 bg-white border border-gray-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-emerald-500"
              />
              {erroValidacaoHorario && (
                <p className="text-xs text-rose-600 font-semibold pt-1 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{erroValidacaoHorario}</span>
                </p>
              )}
            </div>

            {/* Ação Primária ou Card de Intenção Confirmada */}
            {dados?.confirmou_intencao && !confirmou_presenca ? (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl space-y-3 shadow-2xs">
                <div className="flex items-center gap-2.5 text-xs font-bold text-emerald-950">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>Horário previsto registrado para às {dados.horario || horarioEscolhido}!</span>
                </div>
                <p className="text-xs text-emerald-800 leading-relaxed">
                  Sua confirmação de intenção foi salva com sucesso. Quando você estiver fisicamente na clínica parceira para o atendimento, clique abaixo para registrar sua chegada definitiva com a selfie.
                </p>
                <div className="pt-1 flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={handleSelecionarJaEstouAqui}
                    className="flex-1 py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm rounded-xl shadow-sm transition flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Camera className="w-4 h-4" />
                    <span>Já cheguei na clínica (Tirar Foto)</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmarHorarioPrevisto}
                    disabled={!horarioEscolhido || Boolean(erroValidacaoHorario) || submittingIntencao}
                    className="py-3 px-4 bg-white hover:bg-emerald-50 text-emerald-800 border border-emerald-300 font-bold text-xs rounded-xl shadow-2xs transition flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    {submittingIntencao ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : null}
                    <span>Atualizar Horário</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleConfirmarHorarioPrevisto}
                  disabled={!horarioEscolhido || Boolean(erroValidacaoHorario) || submittingIntencao}
                  className="w-full py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl shadow-sm transition flex items-center justify-center gap-2 cursor-pointer"
                >
                  {submittingIntencao ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Salvando previsão...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Confirmar Horário Previsto ({horarioEscolhido || '--:--'})</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Navegação Inferior: Voltar para Opções ou Recusar */}
            <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => {
                  setErroValidacaoHorario(null);
                  setCurrentStep(2);
                }}
                className="py-2 px-3 text-gray-600 hover:text-gray-900 font-semibold flex items-center gap-1 cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Voltar para as Opções</span>
              </button>

              <button
                type="button"
                onClick={() => setShowRecusaModal(true)}
                className="py-2 px-3 text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-lg font-semibold transition cursor-pointer"
              >
                Não poderei comparecer
              </button>
            </div>
          </div>
        )}

        {/* ETAPA 4: SELFIE DE COMPROVAÇÃO / CONFIRMAÇÃO DEFINITIVA */}
        {(confirmou_presenca || currentStep === 4) && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 shadow-sm space-y-5 animate-in fade-in duration-200">
            <div className="flex items-start gap-3.5 pb-4 border-b border-gray-100">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                  confirmou_presenca
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-emerald-600 text-white'
                }`}
              >
                {confirmou_presenca ? '✓' : '4'}
              </div>

              <div className="flex-1">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-100">
                  {confirmou_presenca
                    ? 'Confirmação Definitiva'
                    : 'Etapa 4 • Selfie de Confirmação'}
                </span>
                <h2 className="text-lg font-bold text-gray-900 mt-2">
                  {confirmou_presenca
                    ? 'Presença Confirmada'
                    : 'Tirar Foto para Confirmar'}
                </h2>
                <p className="text-xs text-gray-600 mt-1">
                  {confirmou_presenca
                    ? 'Sua presença foi confirmada e o registro é definitivo no sistema.'
                    : tipoPresenca === 'ja_chegou'
                    ? `Chegada registrada às ${horarioEscolhido || currentTimeStr}. Tire sua selfie para concluir a presença.`
                    : `Comparecimento previsto para às ${horarioEscolhido || 'hoje'}. Tire sua selfie para confirmar seu agendamento.`}
                </p>
              </div>
            </div>

            {/* Informações da escolha com botão de alteração */}
            <div className="p-3 bg-emerald-50/70 border border-emerald-200/80 rounded-xl text-xs flex items-center justify-between text-emerald-950">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-emerald-600" />
                <span>
                  <strong>Horário registrado:</strong>{' '}
                  {horarioEscolhido || currentTimeStr} (
                  {tipoPresenca === 'ja_chegou' ? 'Já na clínica' : 'Horário previsto'})
                </span>
              </div>
              {!confirmou_presenca && (
                <button
                  type="button"
                  onClick={() => {
                    stopCamera();
                    if (tipoPresenca === 'horario_previsto') {
                      setCurrentStep(3);
                    } else {
                      setCurrentStep(2);
                    }
                  }}
                  className="text-emerald-700 hover:underline font-semibold text-[11px] cursor-pointer"
                >
                  Alterar
                </button>
              )}
            </div>

            {confirmou_presenca ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2.5 text-xs font-semibold text-emerald-800 bg-emerald-50 px-4 py-3.5 rounded-xl border border-emerald-200">
                  <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span>
                    Presença confirmada no sistema com sucesso!
                    {dados.horario && (
                      <strong className="block text-emerald-950 sm:inline sm:ml-1">
                        ({dados.tipo_confirmacao === 'ja_chegou' ? 'Chegada às ' : 'Previsão às '}
                        {dados.horario})
                      </strong>
                    )}
                  </span>
                </div>

                {dados.selfie_path && (
                  <div className="text-center pt-2">
                    <img
                      src={
                        dados.selfie_path.startsWith('blob:') || dados.selfie_path.startsWith('data:')
                          ? dados.selfie_path
                          : token && (dados.selfie_path.includes('storage.googleapis.com') || !dados.selfie_path.startsWith('http'))
                          ? `${getCustomerApiBaseURL().replace(/\/customer$/, '')}/customer/presenca/${token}/selfie`
                          : dados.selfie_path.startsWith('http')
                          ? dados.selfie_path
                          : `${getCustomerApiBaseURL().replace(/\/customer$/, '')}${dados.selfie_path.startsWith('/') ? '' : '/'}${dados.selfie_path}`
                      }
                      onError={(e) => {
                        if (token && !e.currentTarget.src.includes(`/customer/presenca/${token}/selfie`)) {
                          e.currentTarget.src = `${getCustomerApiBaseURL().replace(/\/customer$/, '')}/customer/presenca/${token}/selfie`;
                        }
                      }}
                      alt="Selfie de Presença"
                      className="w-40 h-40 object-cover rounded-2xl mx-auto border-2 border-emerald-400 shadow-md"
                    />
                    <span className="text-[11px] text-gray-500 mt-1.5 block">
                      Foto registrada para auditoria
                    </span>
                  </div>
                )}

                {!isEmbedded ? (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleVoltarParaListagem}
                      className="w-full py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs sm:text-sm rounded-xl shadow-sm transition flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Voltar para Meus Exames</span>
                    </button>
                  </div>
                ) : (
                  <div className="p-3.5 bg-slate-100 rounded-xl text-center text-xs text-slate-600 mt-2">
                    Presença confirmada no sistema! Você já pode fechar esta tela no WhatsApp.
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {cameraError && (
                  <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2.5 shadow-2xs">
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-amber-950">Aviso sobre a câmera</p>
                      <p className="text-[11px] text-amber-800 mt-0.5 leading-relaxed">{cameraError}</p>
                    </div>
                  </div>
                )}

                {/* Visor de Câmera Stream ou Prévia da Foto */}
                {isCameraActive ? (
                  <div className="relative rounded-2xl overflow-hidden bg-slate-950 aspect-square max-w-sm mx-auto shadow-md border-2 border-emerald-500/60">
                    <video
                      ref={videoCallbackRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover scale-x-[-1]"
                    />

                    {cameraStarting && (
                      <div className="absolute inset-0 bg-slate-950/85 flex flex-col items-center justify-center text-white gap-2 z-20">
                        <RefreshCw className="w-7 h-7 text-emerald-400 animate-spin" />
                        <span className="text-xs font-medium">Iniciando câmera...</span>
                      </div>
                    )}

                    {!cameraStarting && (
                      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                        <div className="w-48 h-60 border-2 border-white/40 border-dashed rounded-[50%] shadow-2xs" />
                      </div>
                    )}

                    <div className="absolute bottom-4 inset-x-0 flex justify-center gap-3 z-30">
                      <button
                        type="button"
                        onClick={capturePhotoFromStream}
                        disabled={cameraStarting}
                        className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50 text-white rounded-full text-xs font-bold shadow-lg flex items-center gap-2 transition cursor-pointer"
                      >
                        <Camera className="w-4 h-4" />
                        <span>Tirar Foto</span>
                      </button>
                      <button
                        type="button"
                        onClick={stopCamera}
                        className="px-4 py-2.5 bg-slate-900/80 hover:bg-slate-900 text-white rounded-full text-xs font-semibold transition cursor-pointer backdrop-blur-xs"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : selfiePreviewUrl ? (
                  <div className="text-center space-y-3">
                    <div className="relative inline-block">
                      <img
                        src={selfiePreviewUrl}
                        alt="Prévia da selfie"
                        className="w-44 h-44 object-cover rounded-2xl border-2 border-emerald-500 shadow-md mx-auto"
                      />
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelfieBlob(null);
                          setSelfiePreviewUrl(null);
                          startCamera();
                        }}
                        className="text-xs text-gray-700 hover:text-emerald-700 font-semibold px-4 py-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 shadow-2xs transition cursor-pointer"
                      >
                        Tirar outra foto
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-emerald-200 rounded-2xl p-6 text-center bg-emerald-50/30">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3 text-emerald-700">
                      <Camera className="w-6 h-6" />
                    </div>
                    <p className="text-xs font-semibold text-gray-800 mb-1">
                      Tirar Selfie para Confirmação
                    </p>
                    <p className="text-[11px] text-gray-500 mb-4 max-w-xs mx-auto">
                      Tire uma foto rápida do seu rosto para registrar sua confirmação no sistema.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
                      <button
                        type="button"
                        onClick={startCamera}
                        className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-sm transition flex items-center justify-center gap-2 cursor-pointer"
                      >
                        <Camera className="w-4 h-4" />
                        <span>Abrir Câmera</span>
                      </button>

                      <label className="px-4 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-xl text-xs font-semibold shadow-2xs transition cursor-pointer flex items-center justify-center gap-2">
                        <span>Selecionar Foto</span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          capture="user"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                )}

                {/* Botão de Envio e Comprovação Final */}
                {selfieBlob && (
                  <button
                    onClick={handleComprovarPresenca}
                    disabled={submittingPresenca}
                    className="w-full py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold shadow-sm transition flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {submittingPresenca ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Enviando foto e confirmando...</span>
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-5 h-5" />
                        <span>Confirmar e Finalizar</span>
                      </>
                    )}
                  </button>
                )}

                {/* Navegação Inferior para Voltar */}
                <div className="pt-2 flex items-center justify-between border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      stopCamera();
                      if (tipoPresenca === 'horario_previsto') {
                        setCurrentStep(3);
                      } else {
                        setCurrentStep(2);
                      }
                    }}
                    className="py-2.5 px-4 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 font-semibold text-xs rounded-xl shadow-2xs transition flex items-center gap-1.5 cursor-pointer"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>
                      {tipoPresenca === 'horario_previsto' ? 'Voltar ao Horário' : 'Voltar às Opções'}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      stopCamera();
                      setCurrentStep(1);
                    }}
                    className="text-xs text-gray-500 hover:text-emerald-700 font-medium transition cursor-pointer"
                  >
                    Ver dados gerais
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modal de confirmação de ausência */}
      {showRecusaModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-rose-600">
              <AlertCircle className="w-6 h-6 shrink-0" />
              <h3 className="text-base font-bold text-gray-900">
                Não poderei comparecer
              </h3>
            </div>

            <p className="text-xs text-gray-600 leading-relaxed">
              Tem certeza que deseja registrar sua ausência? O setor de Recursos Humanos da sua empresa será notificado sobre a falta.
            </p>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Motivo (opcional):
              </label>
              <textarea
                value={motivoRecusa}
                onChange={(e) => setMotivoRecusa(e.target.value)}
                placeholder="Ex: Imprevisto de saúde, urgência pessoal..."
                rows={2}
                className="w-full p-2.5 text-xs bg-slate-50 border border-gray-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-rose-500"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowRecusaModal(false)}
                className="flex-1 py-2.5 px-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 font-semibold text-xs rounded-xl shadow-2xs transition cursor-pointer"
              >
                Voltar
              </button>

              <button
                type="button"
                onClick={handleRecusarPresenca}
                disabled={submittingRecusa}
                className="flex-1 py-2.5 px-3 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-sm transition flex items-center justify-center gap-1.5 cursor-pointer"
              >
                {submittingRecusa ? 'Enviando...' : 'Confirmar Falta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rodapé Oficial SST Exames */}
      <footer className="border-t border-gray-200 bg-white py-4 text-center text-xs text-gray-500">
        <div className="max-w-2xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>SST Exames • Gestão Integrada de Saúde Ocupacional</span>
          <span className="text-[11px] text-gray-400">Ambiente Seguro & Criptografado</span>
        </div>
      </footer>
    </div>
  );
}
